import styled from '@emotion/styled';

import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { Field, Form } from 'formik';
import { updateTourStepForm } from 'data_system/resource_portal/actions/experienceActions';
import { Theme } from 'ui_system/theme';

import RichText from 'ui_system/forms/RichText';
import ErrorFocus from 'views/anyguide/resources/components/ErrorFocus';
import RadioPair from 'ui_system/forms/RadioPair';
import SuppliesList from 'ui_system/forms/ItemList';
import SimpleTextarea from 'ui_system/forms/SimpleTextarea';
import { BaseSelect } from 'ui_system/forms/BaseSelect';

import FormSubmitButton from 'views/resource_portal/routes/experiences/ui/FormSubmitButton';
import { t, translateWithObject } from 'services/translation';

import {
  FormContainer,
  FieldSection,
  Label,
  Actions,
  CapLabel,
  Error,
  TextField,
  SubLabel,
  DividingLine,
} from 'ui_system/forms/FormStyles';

import Popover from 'ui_system/popovers/Popover';

const DetailsSection = styled.div`
  margin-top: 30px;
  padding-top: 30px;
  border-top: solid 1px ${Theme.colors.lightLine};
`;

const DescriptionSection = styled.div`
  margin-bottom: 30px;
`;

const DescriptionSubLabel = styled.div`
  color: ${Theme.colors.lightText};
`;

const NotesField = styled(SimpleTextarea)`
  padding-top: 1px;
  width: 100%;
`;

const ExampleMessage = styled.div`
  box-shadow: 0px 4px 9px ${Theme.colors.inputs.border};
  padding: 20px 15px;
  background: white;
  font-family: 'Proxima-Nova', Helvetica, sans-serif;
  font-size: 14px;
`;

const ExampleLink = styled.a`
  color: ${(props) => props.linkColor};

  :hover {
    color: ${(props) => props.linkColor};
  }
`;

// Prop helpers ==========
const textFieldProps = {
  css: TextField,
  type: 'text',
};
// ========== Prop helpers
// Form content
class AboutForm extends React.Component {
  constructor(props) {
    super(props);
    this.t = translateWithObject(props.partner.translations);
  }

  componentDidMount() {
    this.props.updateTourStepForm(null);
  }

  setFieldValue = (name, value) => {
    const {
      tourStepForm,
      updateTourStepForm: dispatchUpdateTourStepForm,
      setFieldValue: formikSetFieldValue,
    } = this.props;
    if (!tourStepForm) dispatchUpdateTourStepForm(this.props);
    formikSetFieldValue(name, value);
  };

  /*
    Custom Fields ->
  */
  updateCustomList = (name, value) => {
    const newList = {
      experienceQuestionId: name.replace('customField', ''),
      questionAnswers: value,
    };
    this.setFieldValue(name, newList);
  };

  updateText = (fieldName, value) => {
    const { values } = this.props;
    // const fieldName = `customField${customField.id}`;

    const answerId =
      values[fieldName] &&
      values[fieldName].questionAnswers &&
      values[fieldName].questionAnswers.length > 0
        ? values[fieldName].questionAnswers[0].id
        : null;

    const newText = {
      experienceQuestionId: fieldName.replace('customField', ''),
      textAnswer: value,
      answerId,
    };

    this.setFieldValue(fieldName, newText);
  };

  renderOptionalText = (customField) => {
    const { values, i18n } = this.props;
    const fieldName = `customField${customField.id}`;
    const text = values[fieldName] ? values[fieldName].textAnswer : '';
    const title = customField.textTranslations[i18n] || customField.text;
    const subtitle =
      customField.subtextTranslations[i18n] || customField.subtext;

    return (
      <Fragment>
        <Label allowWrap>{title}</Label>
        <SubLabel allowWrap>{subtitle}</SubLabel>
        <Field
          name={fieldName}
          component={NotesField}
          field={{ name: fieldName, value: text }}
          maxLength={500}
          hidePlaceholderIcon
          height={100}
          focusHeight={100}
          fontSize={14}
          handleTextChange={this.updateText}
          placeholder={' '}
          fullborder
        />
      </Fragment>
    );
  };

  renderText = (customField) => {
    const { errors, values, i18n } = this.props;
    const fieldName = `customField${customField.id}`;
    const text = values[fieldName] ? values[fieldName].textAnswer : '';
    const title = customField.textTranslations[i18n] || customField.text;
    const subtitle =
      customField.subtextTranslations[i18n] || customField.subtext;

    return (
      <Fragment>
        <Label allowWrap>{title}</Label>
        <SubLabel allowWrap>{subtitle}</SubLabel>
        <Field
          name={fieldName}
          render={(_field) => (
            <input
              type="text"
              value={text}
              onChange={(e) => this.updateText(fieldName, e.target.value)}
              {...textFieldProps}
            />
          )}
        />
        {errors[fieldName] && <Error>{errors[fieldName].textAnswer}</Error>}
      </Fragment>
    );
  };

  handleRadioToggle = (name, value, fieldName, itemObj) => {
    if (value) {
      this.updateCustomList(fieldName, [Object.assign(itemObj)]);
      this.setFieldValue(name, value);
    } else {
      this.updateCustomList(fieldName, []);
      this.setFieldValue(name, value);
    }
  };

  renderList = (customField) => {
    const { values, brand, errors, i18n } = this.props;
    const { brandColor } = brand;
    const fieldName = `customField${customField.id}`;
    const toggleVisibleFieldName = `toggleCustomField${customField.id}`;
    const itemObj = {
      id: null,
      textAnswer: '',
      textAnswerPlaceholder: t('ITEM_NAME'),
      quantity: '',
      quantityPlaceholder: t('QUANTITY'),
      supplyUrl: '',
      supplyUrlPlaceholder: t('OPTIONAL_URL'),
    };
    const answers = values[fieldName] ? values[fieldName].questionAnswers : [];
    const title = customField.textTranslations[i18n] || customField.text;
    const subtitle =
      customField.subtextTranslations[i18n] || customField.subtext;

    return (
      <Fragment>
        <CapLabel>{t('WHAT_TO_BRING')}</CapLabel>
        <SubLabel allowWrap>
          {`${t(
            'LIST_ANY_ITEMS_OR_SUPPLIES_THAT_PARTICIPANTS_WILL_NEED_TO_BRING_WITH_THEM_TO_THE_CLASS'
          )}
          ${t(
            'THIS_WILL_BE_DISPLAYED_IN_THE_CLASS_DESCRIPTION_AND_EMAIL_COMMUNICATIONS_TO_PARTICIPANTS'
          )}`}
        </SubLabel>
        <Label allowWrap>
          {t('WILL_YOUR_PARTICIPANTS_NEED_TO_BRING_ANYTHING_FOR_THIS_CLASS')}
        </Label>
        <RadioPair
          field={{
            name: toggleVisibleFieldName,
            value: values[toggleVisibleFieldName] || false,
          }}
          labels={[t('YES'), t('NO')]}
          values={[true, false]}
          handleToggle={(value, name) =>
            this.handleRadioToggle(value, name, fieldName, itemObj)
          }
          format="minimal"
          color={brandColor}
        />
        {values[toggleVisibleFieldName] && (
          <Fragment>
            <DividingLine margintop />
            <Field
              name={fieldName}
              items={answers}
              itemObj={itemObj}
              title={title}
              subTitle={subtitle}
              onUpdate={this.updateCustomList}
              component={SuppliesList}
              color={brandColor}
            />
          </Fragment>
        )}
        {errors[fieldName] &&
          errors[fieldName].questionAnswers.filter((el) => !!el)[0] && (
            <Error>
              {
                errors[fieldName].questionAnswers.filter((el) => !!el)[0]
                  .textAnswer
              }
            </Error>
          )}
        <DividingLine margintop />
      </Fragment>
    );
  };

  renderSelect = (customField) => {
    const { values, errors, i18n } = this.props;
    const fieldName = `customField${customField.id}`;
    const options = customField.questionOptions.data.map((qo) => {
      const label = qo.attributes.textTranslations[i18n] || qo.attributes.text;
      const selected = values[fieldName].questionOptionId === qo.attributes.id;
      return {
        label,
        selected,
        value: {
          experienceQuestionId: customField.id,
          questionOptionId: qo.attributes.id,
        },
      };
    });
    const selected = options.filter((option) => option.selected)[0];
    const title = customField.textTranslations[i18n] || customField.text;
    const subtitle =
      customField.subtextTranslations[i18n] || customField.subtext;

    return (
      <Fragment>
        <Label allowWrap>{title}</Label>
        <SubLabel allowWrap>{subtitle}</SubLabel>
        <Field
          name={fieldName}
          component={BaseSelect}
          placeholder={t('SELECT')}
          value={selected}
          options={options}
          onChange={(val) => this.setFieldValue(fieldName, val.value)}
          styles={{ container: (_state) => ({ width: '100%' }) }}
        />
        {errors[fieldName] && (
          <Error>{errors[fieldName].questionOptionId}</Error>
        )}
      </Fragment>
    );
  };

  renderCustomField = (customField) => {
    return (
      <Fragment key={customField.id}>
        <FieldSection>
          {/* Use switch */}
          {customField.inputType === 'select' && this.renderSelect(customField)}
          {customField.inputType === 'list' && this.renderList(customField)}
          {customField.inputType === 'text' && this.renderText(customField)}
          {customField.inputType === 'list_open_text' &&
            this.renderOptionalText(customField)}
        </FieldSection>
      </Fragment>
    );
  };

  renderCustomFields = () => {
    const { experience } = this.props;
    const { customFields } = experience;
    const additionalDetails = customFields.data.filter(
      (q) =>
        q.attributes &&
        (q.attributes.inputType === 'select' ||
          q.attributes.inputType === 'text')
    );
    const supplyLists = customFields.data.filter(
      (q) =>
        q.attributes &&
        (q.attributes.inputType === 'list' ||
          q.attributes.inputType === 'list_open_text')
    );

    return (
      <DetailsSection>
        {additionalDetails.length > 0 && (
          <Fragment>
            <CapLabel>{t('ADDITIONAL_DETAILS')}</CapLabel>
            {additionalDetails.map((q) => this.renderCustomField(q.attributes))}
            <DividingLine marginbottom />
          </Fragment>
        )}
        {supplyLists.length > 0 && (
          <Fragment>
            {supplyLists.map((q) => this.renderCustomField(q.attributes))}
          </Fragment>
        )}
      </DetailsSection>
    );
  };

  //

  render() {
    const { tourStepForm, errors, brand, i18n } = this.props;
    const { linkColor } = brand;

    return (
      <Form>
        <ErrorFocus />
        <FormContainer>
          <Label>{this.t('NAME_OF_THE_CLASS')}</Label>
          <FieldSection>
            <Field
              {...textFieldProps}
              onChange={(e) => this.setFieldValue('name', e.target.value)}
              name="name"
              placeholder="Experience Name"
            />
            {errors.name && <Error>{errors.name}</Error>}
          </FieldSection>

          <Label>{this.t('SUBTITLE_FOR_THE_CLASS')}</Label>
          <SubLabel block>
            {`${t(
              'PLEASE_INCLUDE_SKILL_LEVEL_ALL_LEVEL_PAINTING_CLASS_WITH_FOCUS_ON'
            )}...`}
          </SubLabel>
          <FieldSection>
            <Field
              {...textFieldProps}
              onChange={(e) => this.setFieldValue('tagline', e.target.value)}
              name="tagline"
              placeholder=""
            />
            {errors.tagline && <Error>{errors.tagline}</Error>}
          </FieldSection>

          <div style={{ position: 'relative ' }}>
            <Label>{this.t(`EXPERIENCE_NAME_SINGULAR_DESCRIPTION`)}</Label>
            <SubLabel block allowWrap>
              <DescriptionSubLabel>
                {this.t(
                  'A_BRIEF_DESCRIPTION_OF_WHAT_YOU_LL_BE_MAKING_IN_YOUR_CLASS'
                )}
              </DescriptionSubLabel>

              <Popover
                boundary="window"
                placement="bottom-start"
                content={
                  <ExampleMessage>
                    {t(
                      "CREATE_YOUR_OWN_MARBLED_ART_AND_BRIGHTEN_UP_YOUR_FALL_DECOR_WITH_A_SWIRL_OF_COLORS!_WE'LL_LEARN_THE_BASICS_OF_PAINT_POURING_SO_YOU_CAN_MARBLE_YOUR_CANVAS_IN_A_FEW_SIMPLE_STEPS_ITS_EASY_AND_FUN_WE_PROMISE"
                    )}
                  </ExampleMessage>
                }
              >
                <span>
                  <ExampleLink linkColor={linkColor}>
                    {this.t('VIEW_EXAMPLE')}
                  </ExampleLink>
                </span>
              </Popover>
            </SubLabel>
          </div>
          <DescriptionSection>
            <Field
              name="description"
              handleChange={this.setFieldValue}
              supportImages={false}
              component={RichText}
            />
            {errors.description && <Error>{errors.description}</Error>}
          </DescriptionSection>
          {this.renderCustomFields()}

          <Actions border>
            <FormSubmitButton dirty={tourStepForm} i18n={i18n} />
          </Actions>
        </FormContainer>
      </Form>
    );
  }
}

const mapStateToProps = (state) => {
  const { experienceReducer, initialProps } = state;

  return {
    experience: experienceReducer.experience,
    experienceUpdating: experienceReducer.experienceUpdating,
    allStepsCompleted: experienceReducer.allStepsCompleted,
    tourStepForm: experienceReducer.tourStepForm,
    brand: initialProps.brand,
    partner: initialProps.partner,
  };
};

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators({ updateTourStepForm }, dispatch);
};

export default connect(mapStateToProps, mapDispatchToProps)(AboutForm);
